import { ArrowDownTrayIcon, ArrowUpTrayIcon, CheckIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { yupResolver } from "@hookform/resolvers/yup";
import Switch from "components/atoms/Switch";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { differenceWith, isEqual } from "lodash";
import { ChangeEventHandler, MouseEventHandler, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { get, post } from "utils/request";
import { useDataProviderContext } from "pages/MagnetDetails/DataProvider";
import { getExportExclusiveMembersRoute, getUpdateExclusiveMembersRoute } from "utils/helpers";
import { useLoaderContext } from "components/context/LoaderContext";
import { toast } from "react-toastify";
import { downloadOnClick } from "utils";
import UploadCSVModalForExclusiveMembers from "./UploadCSVModalForExclusiveMembers";
import { TOAST_CONFIG } from "../../../../constants";

const MembersFormSchema = yup.object().shape({
  members: yup.array().of(
    yup.object().shape({
      email: yup.string().email("Invalid email").required("Required field"),
      status: yup.string().required("Required field"),
    }),
  ),
});

function MemberComponent({
  email,
  status,
  unsaved,
  onStatusChange,
  onEmailChange,
  onRemove,
  error,
}: ExclusiveMemeber & {
  onStatusChange: MouseEventHandler;
  onEmailChange?: ChangeEventHandler<HTMLInputElement>;
  unsaved?: boolean;
  onRemove?: MouseEventHandler;
  error?: string;
}) {
  return (
    <div className="relative flex items-center justify-between border-b-1 border-slate-600 border-opacity-40 pb-2">
      <div className="grow pr-8">
        <input
          title={email}
          type="text"
          className="w-full rounded-lg bg-primary px-2 py-1 text-xxs disabled:truncate disabled:border-none disabled:text-xs"
          placeholder="Enter email"
          value={email}
          disabled={!unsaved}
          onChange={unsaved ? onEmailChange : () => {}}
        />
        {error && <ErrorMessage errorMessage={error} />}
      </div>
      <Switch
        checked={status === "active"}
        onChange={onStatusChange}
        label={status === "active" ? "Active" : "In-Active"}
      />
      {unsaved && (
        <button
          type="button"
          className="absolute -right-12 rounded-[50%] border-1 border-secondary bg-opacity-80 p-0.5 text-secondary transition hover:bg-secondary hover:bg-opacity-100 hover:text-white active:scale-75"
          onClick={onRemove}
        >
          <XMarkIcon className="h-4 w-4 " />
        </button>
      )}
    </div>
  );
}

const handleExportToCSV = async (brandId: string, magnetId: string) => {
  try {
    const resp: Blob = await get(`${getExportExclusiveMembersRoute(brandId, magnetId)}`, {
      requestConfig: {
        responseType: "blob",
      },
      headers: {
        "Content-Type": "application/octate-stream",
      },
    });
    downloadOnClick(URL.createObjectURL(resp));
  } catch {
    toast.error("Something went wrong while trying to Export Members Data", TOAST_CONFIG);
  }
};

function ExclusiveMembersComponent({
  membersList,
  refetchMembersData,
}: {
  membersList: ExclusiveMembers;
  refetchMembersData: any;
}) {
  const [isUploadCSVModalOpen, setIsUploadCSVModalOpen] = useState(false);
  const { magnetDetailsState } = useDataProviderContext();
  const { setLoading } = useLoaderContext();

  const {
    watch,
    control,
    setValue,
    formState: { errors },
    getValues,
    setError,
    trigger,
  } = useForm<{ members: ExclusiveMembers & { unsaved?: boolean } }>({
    defaultValues: { members: membersList },
    resolver: yupResolver(MembersFormSchema),
  });
  const { fields: members, append, remove } = useFieldArray({ control, name: "members" });
  const areUnsavedChanges = useMemo(
    () => !isEqual(membersList, watch("members")),
    [JSON.stringify(membersList), JSON.stringify(watch("members"))],
  );

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (areUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [areUnsavedChanges]);

  const validateUniqueness = async () => {
    await trigger("members");
    const lastIndexMap: any = {};
    const errorIndices: Set<number> = new Set();
    const tempMembers = getValues("members");
    tempMembers.forEach((member, index) => {
      if (member.email) {
        if (lastIndexMap[member.email] !== undefined) {
          errorIndices.add(lastIndexMap[member.email]);
          errorIndices.add(index);
        }
        lastIndexMap[member.email] = index;
      }
    });
    errorIndices.forEach((index) => {
      setError(`members.${index}.email`, { message: "Duplicate entry" });
    });
  };

  const handleAddMember = () => {
    append({ email: "", status: "active", unsaved: true });
    validateUniqueness();
  };

  const handleRemoveMember = (index: number) => {
    remove(index);
    validateUniqueness();
  };

  const saveUpdatesHandler = async () => {
    await validateUniqueness();
    if (!errors.members?.length) {
      const formData = getValues();
      const payloadWithoutExtraKeys = formData.members.map(({ email, status }) => ({ email, status }));
      const payload = { members: differenceWith(payloadWithoutExtraKeys, membersList, isEqual) };
      return post(
        getUpdateExclusiveMembersRoute(magnetDetailsState?.brand_id as string, magnetDetailsState?.id as string),
        payload,
        {
          processData: () => {
            setLoading(false);
            refetchMembersData();
            toast.success("Updates Saved!", TOAST_CONFIG);
          },
          processError: () => {
            setLoading(false);
            toast.error("Failed to add members!", TOAST_CONFIG);
          },
        },
      );
    }
    return null;
  };

  return (
    <div className="flex h-full w-3/6 flex-col justify-between">
      <UploadCSVModalForExclusiveMembers
        onSubmit={refetchMembersData}
        open={isUploadCSVModalOpen}
        onClose={() => {
          setIsUploadCSVModalOpen(false);
        }}
      />
      <div>
        <div className="mt-2 flex flex-col gap-3 pt-2 text-white">
          <div className="flex justify-between text-sm font-bold underline">
            <span>Member Email</span>
            <span>Status</span>
          </div>
          {members.map((member, index) => (
            <MemberComponent
              email={watch(`members.${index}.email`)}
              status={watch(`members.${index}.status`)}
              onStatusChange={() => {
                setValue(
                  `members.${index}.status`,
                  watch(`members.${index}.status`) === "active" ? "inactive" : "active",
                );
              }}
              onEmailChange={(e) => {
                setValue(`members.${index}.email`, e.target.value, { shouldValidate: true });
              }}
              onRemove={() => handleRemoveMember(index)}
              unsaved={member.unsaved}
              // @ts-ignore
              error={errors?.members?.[index]?.email?.message}
              // eslint-disable-next-line react/no-array-index-key
              key={member.email + index}
            />
          ))}
        </div>
        <div className="my-3 flex justify-end">
          <button
            type="button"
            onClick={handleAddMember}
            title="Add member"
            className="inline-flex items-center gap-x-0.5 rounded-md border-1 border-secondary bg-transparent px-2 py-1 text-base font-semibold text-secondary shadow-sm hover:bg-secondary hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <PlusIcon className="-ml-0.5 h-4 w-4" aria-hidden="true" />
            Add Member
          </button>
        </div>
      </div>
      <div className="bottom-0 flex justify-end gap-2 bg-primary py-5">
        <button
          type="button"
          disabled={areUnsavedChanges}
          onClick={() => {
            setIsUploadCSVModalOpen(true);
          }}
          title={areUnsavedChanges ? "Save or revert unsaved changes" : "Add members in bulk by uploading a CSV file"}
          className="mr-1.5 inline-flex items-center gap-x-1.5 rounded-md border-1 border-secondary bg-transparent px-3 py-2 text-xxs font-semibold text-secondary shadow-sm hover:bg-secondary hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:bg-secondary disabled:bg-opacity-20 disabled:text-secondary disabled:opacity-50"
        >
          <ArrowUpTrayIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Add Members via CSV
        </button>
        <button
          type="button"
          onClick={() => handleExportToCSV(magnetDetailsState?.brand_id as string, magnetDetailsState?.id as string)}
          title="Click to export members to a CSV file"
          className="mr-1.5 inline-flex items-center gap-x-1.5 rounded-md border-1 border-secondary bg-transparent px-3 py-2 text-xxs font-semibold text-secondary shadow-sm hover:bg-secondary hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <ArrowDownTrayIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Export Members List
        </button>
        <button
          type="button"
          onClick={saveUpdatesHandler}
          title={areUnsavedChanges ? "Click to save changes" : "No changes to save"}
          disabled={!areUnsavedChanges}
          className="mr-1.5 inline-flex items-center gap-x-1.5 rounded-md border-1 border-accent bg-accent bg-opacity-90 px-3 py-2 text-xxs font-semibold text-primary shadow-sm hover:bg-opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:border-opacity-20 disabled:bg-opacity-30"
        >
          <CheckIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
          Save Updates
        </button>
      </div>
    </div>
  );
}

export default ExclusiveMembersComponent;
